import i18n from "i18next";
import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/lilia-ui/dialog";
import { Button } from "@/components/lilia-ui/button";
import { useTranslation } from "@/utils/hooks/useTranslation";

interface ParsedBlock {
  type: string;
  content: Record<string, unknown>;
}

export type LatexImportMode = "append" | "replace";

interface ImportLatexModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (blocks: ParsedBlock[], mode: LatexImportMode, bib?: string) => void;
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>;
  /**
   * Default mode for the import. EditorPage passes `replace` when the document
   * is effectively empty (one empty paragraph) so users importing a fresh paper
   * don't end up with a stray blank paragraph at the top. Defaults to `append`.
   */
  defaultMode?: LatexImportMode;
}

export function ImportLatexModal({ open, onClose, onImport, fetchWithAuth, defaultMode = "append" }: ImportLatexModalProps) {
  const { t } = useTranslation('dialogs');
  const { t: tCommon } = useTranslation('common');
  const [latex, setLatex] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ blocks: ParsedBlock[]; title?: string; warnings?: string[] } | null>(null);
  const [mode, setMode] = useState<LatexImportMode>(defaultMode);
  const [bib, setBib] = useState("");
  const [showBib, setShowBib] = useState(false);

  // Reset the chosen mode whenever the modal re-opens with a (potentially) new default.
  useEffect(() => {
    if (open) {
      setMode(defaultMode);
      setBib("");
      setShowBib(false);
    }
  }, [open, defaultMode]);

  const handleParse = useCallback(async () => {
    if (!latex.trim()) return;
    setIsLoading(true);
    setError(null);
    setPreview(null);

    try {
      const response = await fetchWithAuth("/api/convert/latex-to-blocks", {
        method: "POST",
        body: JSON.stringify({ latex }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({ message: "Failed to parse LaTeX" }));
        throw new Error(data.message || "Failed to parse LaTeX");
      }

      const data = await response.json();
      setPreview(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsLoading(false);
    }
  }, [latex, fetchWithAuth]);

  const handleImport = useCallback(() => {
    if (!preview?.blocks.length) return;
    onImport(preview.blocks, mode, bib.trim() || undefined);
    setLatex("");
    setBib("");
    setPreview(null);
    setError(null);
    onClose();
  }, [preview, mode, bib, onImport, onClose]);

  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      setLatex("");
      setPreview(null);
      setError(null);
      onClose();
    }
  }, [onClose]);

  const blockTypeSummary = preview?.blocks.reduce<Record<string, number>>((acc, b) => {
    acc[b.type] = (acc[b.type] || 0) + 1;
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="import-latex-modal">
        <DialogHeader>
          <DialogTitle>{t('importLatex.title')}</DialogTitle>
          <DialogDescription>
            {t('importLatex.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <textarea
            data-testid="import-latex-input"
            value={latex}
            onChange={(e) => {
              setLatex(e.target.value);
              setPreview(null);
            }}
            placeholder={"\\section{Introduction}\nYour LaTeX content here...\n\\begin{equation}\nE = mc^2\n\\end{equation}"}
            rows={12}
            className="w-full rounded-lg border border-[var(--lilia-border)] bg-[var(--lilia-bg)] p-3 font-mono text-sm text-[var(--lilia-text)] placeholder:text-[var(--lilia-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--lilia-ring)] resize-y"
            disabled={isLoading}
          />

          {/* Optional BibTeX companion — for papers that use \bibliography{file.bib} */}
          <div>
            <button
              type="button"
              data-testid="import-latex-bib-toggle"
              onClick={() => setShowBib((v) => !v)}
              className="text-[12px] text-[var(--lilia-text-muted)] hover:text-[var(--lilia-text)] flex items-center gap-1 transition-colors"
            >
              <span>{showBib ? "▾" : "▸"}</span>
              {showBib ? "Hide BibTeX" : "Also paste a .bib file (optional)"}
            </button>
            {showBib && (
              <div className="mt-2 flex flex-col gap-1">
                <p className="text-[11px] text-[var(--lilia-text-muted)]">
                  If your .tex uses <code>{`\\bibliography{refs.bib}`}</code>, paste the contents of that .bib file here — we'll import its entries into the bibliography panel.
                </p>
                <textarea
                  data-testid="import-latex-bib"
                  value={bib}
                  onChange={(e) => setBib(e.target.value)}
                  placeholder={"@article{smith2020,\n  author = {Smith, John},\n  title  = {A Great Paper},\n  year   = {2020}\n}"}
                  rows={6}
                  className="w-full rounded-lg border border-[var(--lilia-border)] bg-[var(--lilia-bg)] p-3 font-mono text-sm text-[var(--lilia-text)] placeholder:text-[var(--lilia-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--lilia-ring)] resize-y"
                  disabled={isLoading}
                />
              </div>
            )}
          </div>

          {!preview && (
            <Button
              data-testid="import-latex-parse"
              onClick={handleParse}
              disabled={isLoading || !latex.trim()}
              variant="outline"
              size="sm"
            >
              {isLoading ? t('importLatex.parsing') : t('importLatex.preview')}
            </Button>
          )}

          {error && (
            <p className="text-sm text-red-500" data-testid="import-latex-error">{error}</p>
          )}

          {preview && (
            <div className="rounded-lg border border-[var(--lilia-border)] bg-[var(--lilia-bg)] p-3 text-sm" data-testid="import-latex-summary">
              <p className="font-medium text-[var(--lilia-text)]">
                {t('importLatex.blocksDetected', { count: preview.blocks.length })}
                {preview.title && <span className="text-[var(--lilia-text-muted)]"> — {preview.title}</span>}
              </p>
              {blockTypeSummary && (
                <p className="mt-1 text-[var(--lilia-text-muted)]">
                  {Object.entries(blockTypeSummary).map(([type, count]) => `${count} ${type}`).join(", ")}
                </p>
              )}
              {preview.warnings && preview.warnings.length > 0 && (
                <div className="mt-2 text-xs text-amber-500">
                  {preview.warnings.map((w, i) => <p key={i}>{w}</p>)}
                </div>
              )}
            </div>
          )}

          {preview && (
            <fieldset className="rounded-lg border border-[var(--lilia-border)] bg-[var(--lilia-bg)] p-3">
              <legend className="px-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--lilia-text-muted)]">
                {i18n.t("editor:importLatex.whereToInsert")}
              </legend>
              <label className="flex items-start gap-2 cursor-pointer py-1">
                <input
                  type="radio"
                  name="latex-import-mode"
                  value="replace"
                  data-testid="import-latex-mode"
                  data-mode="replace"
                  checked={mode === "replace"}
                  onChange={() => setMode("replace")}
                  className="mt-0.5"
                />
                <span className="text-sm text-[var(--lilia-text)]">
                  <strong>{i18n.t("editor:importLatex.replaceDocument")}</strong>
                  <span className="block text-xs text-[var(--lilia-text-muted)]">
                    {i18n.t("editor:importLatex.discardTheCurrentContentAnd")}
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer py-1">
                <input
                  type="radio"
                  name="latex-import-mode"
                  value="append"
                  data-testid="import-latex-mode"
                  data-mode="append"
                  checked={mode === "append"}
                  onChange={() => setMode("append")}
                  className="mt-0.5"
                />
                <span className="text-sm text-[var(--lilia-text)]">
                  <strong>{i18n.t("editor:importLatex.appendToEnd")}</strong>
                  <span className="block text-xs text-[var(--lilia-text-muted)]">
                    {i18n.t("editor:importLatex.addTheParsedBlocksAfter")}
                  </span>
                </span>
              </label>
            </fieldset>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" data-testid="import-latex-cancel" onClick={() => handleOpenChange(false)} size="sm">
            {tCommon('cancel')}
          </Button>
          <Button
            data-testid="import-latex-submit"
            onClick={handleImport}
            disabled={!preview?.blocks.length}
            size="sm"
          >
            {tCommon('import')} {preview ? t('importLatex.importBlocks', { count: preview.blocks.length }) : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
